import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { AlertData, Order } from "../Alerts/AlertsUtils/alertsInterfaces";
import {
  getComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";
import { EnhancedClipsTableHead } from "./EnhancedClipsTableHead";
import {
  formatClipReceivedLocal,
  formatDateInUTC,
} from "./ClipsUtilities/formatTimeStamp";
import { handleRowSelected } from "./ClipsUtilities/handleRowSelected";
import { NavigateFunction, useSearchParams } from "react-router-dom";
import { handleButtonShading } from "./ClipsUtilities/handleButtonShading";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { handleReorderIndex } from "../Alerts/AlertsUtils/handleReorderIndex";
import { EnhancedSentinelTableHead } from "./EnhancedSentinelTableHead";
import {
  removeClipsIdParamsFromUrl,
} from "./ClipsUtilities/clipsUrlParams";

export const SentinelTable = ({
  sentinelData,
  setSentinelData,
  setSelectedRowSentinel,
  pageSentinel,
  setPageSentinel,
  setVideoClipLoadingSentinel,
  setVideoURLSentinel,
  scrollToTop,
  selectedRowSentinel,
  setClipsIndexOrderSentinel,
  userFiltersSentinel,
  navigate,
  sentinelFilterApplyToken = 0,
}: {
  sentinelData: any;
  setSentinelData: any;
  setSelectedRowSentinel: any;
  pageSentinel: any;
  setPageSentinel: any;
  setVideoClipLoadingSentinel: any;
  setVideoURLSentinel: any;
  scrollToTop: any;
  selectedRowSentinel: any;
  setClipsIndexOrderSentinel: any;
  userFiltersSentinel: any;
  navigate: NavigateFunction;
  sentinelFilterApplyToken?: number;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("formatted_date");
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const lastAutoPagedSelectionRef = React.useRef<{
    selectedFileId: string | null;
    expectedPage: number | null;
  }>({ selectedFileId: null, expectedPage: null });
  const lastFilterApplyTokenRef = React.useRef(sentinelFilterApplyToken);

  React.useEffect(() => {
    if (sentinelFilterApplyToken === lastFilterApplyTokenRef.current) {
      return;
    }
    lastFilterApplyTokenRef.current = sentinelFilterApplyToken;
    setPageSentinel(0);
    lastAutoPagedSelectionRef.current = {
      selectedFileId: null,
      expectedPage: 0,
    };
  }, [sentinelFilterApplyToken, setPageSentinel]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(sentinelData.length / rowsPerPage) - 1
    );
    if (pageSentinel > maxPage) {
      setPageSentinel(0);
    }
  }, [sentinelData.length, rowsPerPage, pageSentinel, setPageSentinel]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof AlertData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageSentinel(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageSentinel(0);
  };

  const emptyRows =
    pageSentinel > 0
      ? Math.max(0, (1 + pageSentinel) * rowsPerPage - sentinelData.length)
      : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(sentinelData, getComparator(order, orderBy)).slice(
        pageSentinel * rowsPerPage,
        pageSentinel * rowsPerPage + rowsPerPage
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, orderBy, pageSentinel, rowsPerPage]
  );

  React.useEffect(() => {
    const sortedRows = stableSort(sentinelData, getComparator(order, orderBy));
    const selectedIndex = sortedRows.findIndex((row: any) => row.selected);
    const selectedFileId: string | null =
      selectedIndex >= 0 && sortedRows[selectedIndex]?.file_id != null
        ? String(sortedRows[selectedIndex].file_id)
        : null;
    const expectedPage =
      selectedIndex >= 0 ? Math.floor(selectedIndex / rowsPerPage) : 0;

    const hasSelectionChanged =
      selectedFileId !== lastAutoPagedSelectionRef.current.selectedFileId;

    if (selectedFileId && hasSelectionChanged) {
      lastAutoPagedSelectionRef.current = {
        selectedFileId,
        expectedPage,
      };
      if (expectedPage !== pageSentinel) {
        setPageSentinel(expectedPage);
      }
    }
  }, [sentinelData, pageSentinel, rowsPerPage, setPageSentinel]);

  const roundToHundredth = (number: number) => {
    const newNumber = number * 100;
    return Math.round(newNumber * 100) / 100;
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 490 }}>
        <Table
          sx={{ fontFamily: "Mulish", postion: "absolute" }}
          aria-labelledby="clips-table"
          stickyHeader
        >
          <EnhancedSentinelTableHead
            onRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
          />
          <TableBody>
            {visibleRows.map((row: any, index: number) => {
              let sx: any = { cursor: "pointer" };
              if (row.selected) {
                sx = {
                  cursor: "pointer",
                  backgroundColor: "rgba(2, 136, 209, 0.2)",
                };
              }
              return (
                <TableRow
                  hover
                  onClick={() => {
                    scrollToTop();
                    setVideoClipLoadingSentinel(true);
                    setSelectedRowSentinel(row);
                    localStorage.setItem(
                      "clipsSelectedRow",
                      JSON.stringify(row)
                    );
                    handleRowSelected(
                      row,
                      setSelectedRowSentinel,
                      setVideoURLSentinel,
                      setVideoClipLoadingSentinel,
                      sentinelData,
                      setSentinelData,
                      selectedRowSentinel,
                      userFiltersSentinel,
                      "sentinel",
                      userFiltersSentinel.user,
                      navigate
                    );
                    removeClipsIdParamsFromUrl(setSearchParams, searchParams);
                  }}
                  tabIndex={-1}
                  key={row.id}
                  sx={sx}
                >
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.file_id}
                  </TableCell>
                  {/* <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.clip_header_received
                      ? formatDateInUTC(row.clip_header_received)
                      : ""}
                  </TableCell> */}
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {formatClipReceivedLocal(
                      row.clip_header_received,
                      row.clip_received_local,
                      row.site_timezone
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.customer_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.camera_name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >{`${roundToHundredth(row.probability)}%`}</TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontSize: "10px", fontFamily: "mulish" }}
                  >
                    {row.clip_result}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={7} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={sentinelData.length}
        rowsPerPage={rowsPerPage}
        page={pageSentinel}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
